import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useWebSignOut } from "@/hooks/use-web-sign-out";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  LogOut,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  type IntakeStatus,
  openHostedIntake,
  startIntake,
} from "@/lib/intake";
import { useTranslation } from "react-i18next";

interface IntakeGateProps {
  status?: IntakeStatus;
  isLoading?: boolean;
  isError?: boolean;
  isRetrying?: boolean;
  startError?: boolean;
  onRetry: () => void;
  returnTo: string;
}

function GateShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-[180] flex min-h-screen items-center justify-center overflow-y-auto bg-background px-4 py-8 text-foreground"
      data-testid="intake-gate"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute left-1/2 top-0 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[24rem] w-[24rem] rounded-full bg-sky-500/5 blur-3xl" />
      </div>
      <div className="relative w-full max-w-xl">{children}</div>
    </div>
  );
}

function IntakeFooterLinks() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs">
      <Link href="/support" className="text-primary underline-offset-4 hover:underline" data-testid="intake-support-link">
        {t("intake.support")}
      </Link>
      <Link href="/account" className="text-primary underline-offset-4 hover:underline" data-testid="intake-account-link">
        {t("intake.account")}
      </Link>
      <Link href="/subscription" className="text-primary underline-offset-4 hover:underline" data-testid="intake-billing-link">
        {t("intake.billing")}
      </Link>
      <Link href="/legal/privacy" className="text-primary underline-offset-4 hover:underline" data-testid="intake-legal-link">
        {t("intake.privacy")}
      </Link>
    </div>
  );
}

export function IntakeGate({
  status,
  isLoading = false,
  isError = false,
  isRetrying = false,
  startError = false,
  onRetry,
  returnTo,
}: IntakeGateProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { handleSignOut, isSigningOut, signOutError } = useWebSignOut();
  const [starting, setStarting] = useState(false);
  const [launchFailed, setLaunchFailed] = useState(startError);

  useEffect(() => {
    setLaunchFailed(startError);
  }, [startError]);

  const launchForm = async () => {
    if (starting) return;
    setStarting(true);
    setLaunchFailed(false);

    // Reserve a new tab while the click still has user activation. The
    // server-issued URL is inserted only after the authenticated request
    // succeeds and is never placed in Plate OS state, storage, or analytics.
    let popup: Window | null = null;
    try {
      popup = window.open("about:blank", "_blank");
      if (popup) popup.opener = null;
    } catch {
      popup = null;
    }

    try {
      const started = await startIntake();
      if (popup) {
        try {
          if (!openHostedIntake(started.url, "_self", popup.document)) {
            throw new Error("The hosted intake URL was refused.");
          }
        } catch {
          popup.close();
          popup = null;
        }
      }

      if (!popup) {
        // The browser blocked the reserved tab. A same-tab navigation is the
        // reliable fallback; Plate OS authentication remains in its own cookie
        // and the Back button returns to the bounded polling page.
        if (!openHostedIntake(started.url, "_self")) {
          throw new Error("The hosted intake URL was refused.");
        }
        return;
      }

      const query = returnTo !== "/dashboard"
        ? `?returnTo=${encodeURIComponent(returnTo)}`
        : "";
      setLocation(`/intake${query}`);
      // Starting an attempt changes the status from not_started to
      // awaiting_verification. Refresh immediately so the bounded polling
      // window is armed even when the member is already on /intake.
      onRetry();
    } catch {
      popup?.close();
      setLaunchFailed(true);
    } finally {
      setStarting(false);
    }
  };

  if (isLoading) {
    return (
      <GateShell>
        <div className="rounded-2xl border border-border bg-card/95 p-8 text-center shadow-2xl">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" aria-label={t("intake.loading")} />
          <p className="text-sm text-muted-foreground" data-testid="intake-loading">
            {t("intake.loading")}
          </p>
          <IntakeFooterLinks />
        </div>
      </GateShell>
    );
  }

  if (isError || !status) {
    return (
      <GateShell>
        <div className="space-y-6 rounded-2xl border border-destructive/30 bg-card/95 p-6 shadow-2xl sm:p-8">
          <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold" data-testid="intake-error-title">
              {t("intake.errorTitle")}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground" data-testid="intake-error-description">
              {t("intake.errorDescription")}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              className="flex-1 gap-2"
              onClick={onRetry}
              disabled={isRetrying}
              data-testid="intake-retry-button"
            >
              <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
              {t("intake.retry")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1 gap-2"
              onClick={() => void handleSignOut()}
              disabled={isSigningOut}
              data-testid="intake-sign-out-button"
            >
              <LogOut className="h-4 w-4" />
              {t("intake.signOut")}
            </Button>
          </div>
          {signOutError && <p role="alert" className="text-sm text-destructive">{t("common.error")}. {t("common.retry")}</p>}
          <IntakeFooterLinks />
        </div>
      </GateShell>
    );
  }

  const statusMessage = {
    not_started: t("intake.notStarted"),
    awaiting_verification: t("intake.awaitingVerification"),
    pending_review: t("intake.pendingReview"),
    completed: t("intake.completed"),
  }[status.state];

  // The state drives the member-facing workflow. A completed state without
  // the server's ready bit is an inconsistent response, so fail closed with
  // the unavailable copy rather than granting access.
  const notReady = status.required && status.state === "completed" && !status.ready;
  const isWaiting = status.state === "awaiting_verification" || status.state === "pending_review";

  return (
    <GateShell>
      <div className="space-y-7 rounded-2xl border border-border bg-card/95 p-6 shadow-2xl sm:p-8">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Plate OS
            </p>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="intake-title">
              {notReady ? t("intake.unavailableTitle") : t("intake.title")}
            </h1>
          </div>
        </div>

        {notReady ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <p className="text-sm leading-relaxed text-muted-foreground" data-testid="intake-not-ready">
              {t("intake.unavailableDescription")}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-muted-foreground" data-testid="intake-description">
              {t("intake.description")}
            </p>

            <div className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/20 p-4">
              {isWaiting ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              ) : (
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              )}
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium" data-testid="intake-status">
                  {statusMessage}
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {isWaiting
                    ? t("intake.waitingDescription")
                    : t("intake.formDescription")}
                </p>
              </div>
            </div>

            {launchFailed && (
              <p className="text-sm text-destructive" role="alert" data-testid="intake-launch-error">
                {t("intake.launchError")}
              </p>
            )}

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                className="flex-1 gap-2"
                onClick={() => void launchForm()}
                disabled={starting}
                data-testid="intake-start-button"
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                {starting
                  ? t("intake.starting")
                  : isWaiting
                    ? t("intake.continueButton")
                    : t("intake.startButton")}
              </Button>
              {isWaiting && (
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={onRetry}
                  disabled={isRetrying}
                  data-testid="intake-check-status-button"
                >
                  <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
                  {t("intake.checkStatus")}
                </Button>
              )}
            </div>
          </>
        )}

        <div className="space-y-3 border-t border-border/60 pt-5">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t("intake.privacyNote")}
          </p>
          <IntakeFooterLinks />
          <Button
            type="button"
            variant="ghost"
            className="h-auto gap-2 px-0 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => void handleSignOut()}
            disabled={isSigningOut}
            data-testid="intake-footer-sign-out"
          >
            <LogOut className="h-3.5 w-3.5" />
            {t("intake.signOut")}
          </Button>
          {signOutError && <p role="alert" className="text-sm text-destructive">{t("common.error")}. {t("common.retry")}</p>}
        </div>
      </div>
    </GateShell>
  );
}